import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// SEO configuration for each page
const SEO_CONFIG = {
  '/': {
    title: 'X-TREME CONCEPT - Property Professional Services | Swansea, South Wales',
    description: 'South Wales Trusted Property Experts. Professional painting, decorating, refurbishments, property maintenance, and handyman services in Swansea. Call 07342 777 907.',
    keywords: 'property services, painting, decorating, refurbishment, handyman, Swansea, South Wales, property maintenance, carpet cleaning',
  },
  '/login': {
    title: 'Login - X-TREME CONCEPT Property Services',
    description: 'Access your X-TREME CONCEPT account. Manage bookings, view invoices, and track your property service appointments.',
    keywords: 'login, customer portal, booking management',
    noindex: true,
  },
  '/register': {
    title: 'Create Account - X-TREME CONCEPT Property Services',
    description: 'Join X-TREME CONCEPT. Create your account to book property services, get quotes, and manage appointments easily.',
    keywords: 'register, create account, sign up, property services',
    noindex: true,
  },
  '/booking': {
    title: 'Book a Service - X-TREME CONCEPT Property Services',
    description: 'Book professional property services online. Choose from painting, decorating, cleaning, and more. Easy online booking system.',
    keywords: 'book service, online booking, schedule appointment, property services booking',
  },
  '/cleaning-prices': {
    title: 'Cleaning Prices - X-TREME CONCEPT Property Services | Swansea',
    description: 'Transparent cleaning prices for homes and businesses in Swansea and South Wales. End of tenancy, deep cleaning, carpet & upholstery cleaning rates.',
    keywords: 'cleaning prices, cleaning rates Swansea, end of tenancy cleaning, deep cleaning cost, carpet cleaning price',
  },
  '/pricing-list': {
    title: 'Pricing List - X-TREME CONCEPT Property Services | Swansea',
    description: 'Full pricing list for property services in Swansea: painting, decorating, refurbishments, maintenance and handyman work. Get a clear quote.',
    keywords: 'pricing list, property services prices, painting cost, decorating prices, handyman rates Swansea',
  },
  '/privacy-policy': {
    title: 'Privacy Policy - X-TREME CONCEPT Property Services',
    description: 'How X-TREME CONCEPT Property Professional Services collects, uses and protects your personal data.',
    keywords: 'privacy policy, data protection',
  },
  '/terms-of-service': {
    title: 'Terms of Service - X-TREME CONCEPT Property Services',
    description: 'Terms and conditions for using X-TREME CONCEPT Property Professional Services.',
    keywords: 'terms of service, terms and conditions',
  },
  '/services': {
    title: 'Our Services - X-TREME CONCEPT Property Services',
    description: 'Explore our full range of property services: painting, decorating, refurbishment, cleaning, handyman services, and more in Swansea.',
    keywords: 'property services, painting services, decorating, refurbishment, cleaning services, handyman',
  },
  '/gallery': {
    title: 'Our Work Gallery - X-TREME CONCEPT Property Services',
    description: 'View our portfolio of completed projects. Before and after transformations of painting, decorating, and property renovations.',
    keywords: 'portfolio, gallery, before after, property transformation, renovation photos',
  },
  '/blog': {
    title: 'Blog & Tips - X-TREME CONCEPT Property Services',
    description: 'Property maintenance tips, DIY guides, and expert advice from X-TREME CONCEPT. Stay updated with the latest home improvement trends.',
    keywords: 'property blog, home improvement tips, DIY guides, property maintenance advice',
  },
  '/contact': {
    title: 'Contact Us - X-TREME CONCEPT Property Services',
    description: 'Get in touch with X-TREME CONCEPT. Call 07342 777 907 or email us for a free quote on property services in Swansea.',
    keywords: 'contact, get quote, phone number, email, Swansea property services',
  },
  '/dashboard': {
    title: 'Dashboard - X-TREME CONCEPT',
    description: 'Manage your property services dashboard.',
    noindex: true,
  },
};

// Default SEO values
const DEFAULT_SEO = {
  title: 'X-TREME CONCEPT - Property Professional Services',
  description: 'South Wales Trusted Property Experts. Professional property services in Swansea.',
  keywords: 'property services, Swansea, South Wales',
  image: 'https://customer-assets.emergentagent.com/job_propertyservice/artifacts/xcvotcdq_logo.webp',
  url: 'https://propertyprofessionalservices.com',
};

// Private routes that must never be indexed (mirrors robots.txt Disallow rules)
const PRIVATE_PREFIXES = [
  '/dashboard', '/admin', '/login', '/register', '/forgot-password',
  '/profile', '/book', '/loyalty', '/payment-reports', '/quotations',
  '/pay/', '/q/', '/truelayer',
];

export const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  image, 
  noindex = false,
  type = 'website',
  article = null 
}) => {
  const location = useLocation();
  
  // Get page-specific SEO or use provided props or defaults
  const pageSeo = SEO_CONFIG[location.pathname] || {};
  const finalTitle = title || pageSeo.title || DEFAULT_SEO.title;
  const finalDescription = description || pageSeo.description || DEFAULT_SEO.description;
  const finalKeywords = keywords || pageSeo.keywords || DEFAULT_SEO.keywords;
  const finalImage = image || DEFAULT_SEO.image;
  const finalUrl = `${DEFAULT_SEO.url}${location.pathname}`;
  const shouldNoIndex = noindex || pageSeo.noindex || PRIVATE_PREFIXES.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    // Update document title
    document.title = finalTitle;

    // Update meta tags
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateMeta('description', finalDescription);
    updateMeta('keywords', finalKeywords);
    
    // Robots
    updateMeta('robots', shouldNoIndex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph
    updateMeta('og:title', finalTitle, true);
    updateMeta('og:description', finalDescription, true);
    updateMeta('og:image', finalImage, true);
    updateMeta('og:url', finalUrl, true);
    updateMeta('og:type', type, true);
    updateMeta('og:site_name', 'X-TREME CONCEPT', true);

    // Twitter Card
    updateMeta('twitter:card', 'summary_large_image', true);
    updateMeta('twitter:title', finalTitle, true);
    updateMeta('twitter:description', finalDescription, true);
    updateMeta('twitter:image', finalImage, true);

    // Article specific (for blog posts)
    if (article) {
      updateMeta('article:published_time', article.publishedTime, true);
      updateMeta('article:modified_time', article.modifiedTime, true);
      updateMeta('article:author', article.author || 'X-TREME CONCEPT', true);
      if (article.tags) {
        article.tags.forEach(tag => {
          updateMeta('article:tag', tag, true);
        });
      }
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', finalUrl);

    // Cleanup function
    return () => {
      // Title will be updated by next page
    };
  }, [finalTitle, finalDescription, finalKeywords, finalImage, finalUrl, shouldNoIndex, type, article]);

  return null; // This component doesn't render anything
};

// Hook for dynamic SEO updates
export const useSEO = (config) => {
  const location = useLocation();
  
  useEffect(() => {
    if (config.title) document.title = config.title;
    
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (meta && content) {
        meta.setAttribute('content', content);
      }
    };

    if (config.description) updateMeta('description', config.description);
    if (config.image) {
      updateMeta('og:image', config.image, true);
      updateMeta('twitter:image', config.image, true);
    }
  }, [config, location]);
};

export default SEOHead;
